import { useState } from "react";
import { WarningCircle, ArrowsClockwise, X } from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * StreamErrorModal — drill-down on the truncated red error string from a
 * failed ingest. Shows the full `last_error`, last_status, and which stream
 * it belongs to, plus a "Retry now" action.
 *
 * Usage: <StreamErrorModal stream={stream} onRetried={refresh} />
 *   The trigger is a clickable warning strip; consumers pass the stream object.
 */
export function StreamErrorStripWithModal({ stream, onRetried, testid = "stream-error-strip" }) {
    const [open, setOpen] = useState(false);
    const [retrying, setRetrying] = useState(false);

    if (!stream?.last_error) return null;

    const retry = async () => {
        setRetrying(true);
        try {
            await api.post(`/streams/${stream.id}/ingest`);
            toast.success("Retry started — give it a minute, then refresh.");
            setOpen(false);
            onRetried?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start retry");
        } finally {
            setRetrying(false);
        }
    };

    return (
        <>
            <button
                type="button"
                data-testid={testid}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                }}
                className="mt-3 text-[11px] text-accent flex items-start gap-1.5 text-left hover:underline focus:outline-none focus:ring-1 focus:ring-accent rounded-sm w-full"
            >
                <WarningCircle size={11} className="shrink-0 mt-0.5" />
                <span className="line-clamp-2 flex-1">{stream.last_error}</span>
                <span className="shrink-0 font-mono uppercase tracking-wider text-[9px] underline-offset-2">View →</span>
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="stream-error-modal"
                    className="rounded-sm max-w-xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <WarningCircle size={18} weight="duotone" className="text-accent" />
                            Ingest failed · {stream.name}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3 text-xs">
                            <div>
                                <div className="overline text-[9px] mb-1">Status</div>
                                <div className="font-mono uppercase text-accent">{stream.last_status || "failed"}</div>
                            </div>
                            <div>
                                <div className="overline text-[9px] mb-1">Last attempt</div>
                                <div className="font-mono">
                                    {stream.last_ingested_at
                                        ? new Date(stream.last_ingested_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                                        : "—"}
                                </div>
                            </div>
                            <div>
                                <div className="overline text-[9px] mb-1">Platforms</div>
                                <div className="font-mono">{(stream.platforms || []).join(", ") || "—"}</div>
                            </div>
                        </div>

                        <div>
                            <div className="overline text-[9px] mb-1.5">Error message</div>
                            <pre
                                data-testid="stream-error-full"
                                className="font-mono text-[11px] leading-relaxed bg-accent/5 border border-accent/30 rounded-sm p-3 whitespace-pre-wrap break-words max-h-[260px] overflow-y-auto text-foreground"
                            >
                                {stream.last_error}
                            </pre>
                        </div>

                        {(stream.topics || []).length > 0 && (
                            <div>
                                <div className="overline text-[9px] mb-1.5">Searching for at time of failure</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(stream.topics || []).slice(0, 8).map((t) => (
                                        <span key={t} className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded-sm">{t}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <p className="text-[11px] text-muted-foreground italic leading-relaxed">
                            Common causes: <strong>Apify monthly usage hard limit exceeded</strong> (raise it in your Apify console),
                            actor rate-limited, source URL no longer reachable, no results for current search terms,
                            or LLM provider quota. The error string above usually points at the cause.
                        </p>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            data-testid="stream-error-close"
                            variant="ghost"
                            size="sm"
                            className="rounded-sm"
                            onClick={() => setOpen(false)}
                        >
                            Close
                        </Button>
                        <Button
                            data-testid="stream-error-retry"
                            size="sm"
                            className="rounded-sm"
                            onClick={retry}
                            disabled={retrying}
                        >
                            {retrying ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" /> : <ArrowsClockwise size={13} className="mr-1.5" />}
                            Retry ingest now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/**
 * StreamErrorBanner — full-width error banner for the top of StreamDetail.
 * Higher-affordance than the card strip: shows full error text inline
 * (no modal needed since you're already on the detail page).
 */
export function StreamErrorBanner({ stream, onRetried, onDismiss }) {
    const [retrying, setRetrying] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    if (!stream?.last_error || dismissed) return null;

    const retry = async () => {
        setRetrying(true);
        try {
            await api.post(`/streams/${stream.id}/ingest`);
            toast.success("Retry started — give it a minute, then refresh.");
            onRetried?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start retry");
        } finally {
            setRetrying(false);
        }
    };

    return (
        <div
            data-testid="stream-error-banner"
            className="mb-6 border border-accent/40 bg-accent/[0.04] rounded-sm"
        >
            <div className="flex items-start gap-3 p-4">
                <WarningCircle size={18} weight="duotone" className="text-accent shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 mb-1.5 flex-wrap">
                        <span className="overline text-accent">Last ingest failed</span>
                        {stream.last_ingested_at && (
                            <span className="font-mono text-[10px] text-muted-foreground">
                                {new Date(stream.last_ingested_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                            </span>
                        )}
                    </div>
                    <pre
                        data-testid="stream-error-banner-text"
                        className="font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words max-h-[120px] overflow-y-auto text-foreground"
                    >
                        {stream.last_error}
                    </pre>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <Button
                        data-testid="stream-error-banner-retry"
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-8 border-accent/40 hover:bg-accent/10"
                        onClick={retry}
                        disabled={retrying}
                    >
                        {retrying ? <ArrowsClockwise size={12} className="mr-1.5 animate-spin" /> : <ArrowsClockwise size={12} className="mr-1.5" />}
                        Retry
                    </Button>
                    <button
                        type="button"
                        data-testid="stream-error-banner-dismiss"
                        onClick={() => { setDismissed(true); onDismiss?.(); }}
                        className="p-1.5 text-muted-foreground hover:text-foreground rounded-sm"
                        aria-label="Dismiss error banner"
                    >
                        <X size={12} weight="bold" />
                    </button>
                </div>
            </div>
        </div>
    );
}
